import React from 'react';
import './CarouselDots.scss';

interface CarouselDotsProps {
  /** How many slides the row holds. A row shorter than the cap gets a dot each. */
  count: number;
  /** How far along the row is, 0 to 1 — which dot lights up. */
  progress: number;
  /** Scroll the row to a fraction of its extent. */
  onSeek: (fraction: number) => void;
  className?: string;
}

// Three, per the Figma card row.
const MAX_DOTS = 3;

/**
 * The pager dots under a horizontal row.
 *
 * The dots mark position along the row, not individual cards: past three
 * slides they split the scroll into equal parts, so the lit dot keeps moving
 * however long the row is. Windowing a dot per card instead left the middle
 * dot lit for the entire scroll of a twenty-card row.
 *
 * Presentational — the caller owns scroll position.
 */
const CarouselDots: React.FC<CarouselDotsProps> = ({
  count,
  progress,
  onSeek,
  className,
}) => {
  const dotCount = Math.min(count, MAX_DOTS);
  if (dotCount < 2) return null;
  const activeIndex = Math.round(progress * (dotCount - 1));

  return (
    <div
      className={`carousel-dots${className ? ` ${className}` : ''}`}
      role="group"
      aria-label="Pagination"
    >
      {Array.from({ length: dotCount }, (_, i) => (
        <button
          type="button"
          key={i}
          className={`carousel-dots__dot${
            i === activeIndex ? ' carousel-dots__dot--active' : ''
          }`}
          aria-label={`${i + 1}`}
          aria-current={i === activeIndex ? 'true' : undefined}
          onClick={() => onSeek(i / (dotCount - 1))}
        />
      ))}
    </div>
  );
};

export default CarouselDots;
